import { useState } from "react"
import type { ReactNode } from "react"
import { Button, Heading, Notice, Text } from "@smooth/design-system-components"
import { DunningDialog } from "../../components/DunningDialog"
import { evaluationError, isRetryableError } from "../errors"
import type { EvaluationSandboxApi } from "../useEvaluationSandbox"
import type {
  ContinuationInfo,
  DraftSource,
  EvaluationCase,
  EvaluationRun,
  GeneratePrompt,
  ImportPreview,
  Terminals,
} from "../types"
import { validateEvaluationCase } from "../validation"
import { modelExemplars, withModelExemplar } from "../expectation"
import { CaseEditForm } from "./CaseEditForm"
import { HEADER_BUTTON_CLASS } from "./CaseFrame"
import type { CaseFrameSlots } from "./CaseFrame"
import { CaseReadView } from "./CaseReadView"
import { ExemplarReference } from "./ExpectationView"
import { GeneratePanel } from "./GeneratePanel"
import { RunHistory } from "./RunHistory"
import { RunResult } from "./RunResult"
import { AlertMessage, Section, StatusMessage } from "./parts"

type Props = {
  initial: EvaluationCase
  api: EvaluationSandboxApi
  terminals: Terminals
  onDirty: (value: boolean) => void
  onBusy: (value: boolean) => void
  onSaved: (record: EvaluationCase) => void
  /** 保存前の下書きの出どころ（督促ケースからの読み込み・次の時点）。注意書きの表示に使う */
  draftSource?: DraftSource | null
  /** 次の時点を作る。表示中の生成結果に本文があれば渡す（無ければ null） */
  onContinue?: (run: EvaluationRun | null) => Promise<void>
  /** 見出しの右に置く操作（しまう・削除の「…」メニュー）。保存前の下書きには無い */
  actions?: ReactNode
  /** ヘッダーの戻る操作（「← 一覧」「← 比較の結果に戻る」） */
  onBack?: () => void
  backLabel?: string
  /** 右のペインの先頭に出す知らせ（しまったケースの知らせ等） */
  notice?: ReactNode
  /** 右のペインの一番上に置くもの（お客様の流れのタイムライン） */
  rightHead?: ReactNode
  /** 期待の先頭に出す、プロンプトの比較の結果 */
  linkedResult?: ReactNode
}

/** 次の時点の下書きで、元のケースと書き込んでほしい発言を知らせる */
function ContinuationNotice({ info }: { info: ContinuationInfo }) {
  return (
    <div role="note" aria-label="次の時点" className="mb-3">
      <Notice tone="info" variant="card">
        <div className="flex flex-col gap-1">
          <Text weight="bold">
            {info.runId != null
              ? `「${info.parentTitle}」（#${info.parentId}）の生成 #${info.runId} の次の時点です（まだ保存していません）`
              : `「${info.parentTitle}」（#${info.parentId}）の次の時点です（まだ保存していません）`}
          </Text>
          <Text size="sm">
            {info.runId != null
              ? `AIドラフトを当社の送信として会話の末尾に足しました。督促チャットの発言 ${info.replyNumber}（末尾のお客様の空の吹き出し）をクリックしてお客様の返事を書き込み、`
              : `督促チャットの発言 ${info.replyNumber - 1}・${info.replyNumber}（末尾の当社とお客様の空の吹き出し）をクリックして当社の送信とお客様の返事を書き込み、`}
            期待する対応と確認ポイント（必要なら期待する道筋）を付けて保存してください。
          </Text>
        </div>
      </Notice>
    </div>
  )
}

/** 読み込んだ下書きで、架空値に置き換えた個人情報と確認のお願いを出す */
function ImportNotice({ info }: { info: ImportPreview }) {
  const masked = info.maskedItems
    .map((item) => `${item.label}（${item.count}件 →「${item.replacement}」）`)
    .join("、")
  return (
    <div role="note" aria-label="読み込み元と個人情報の置き換え" className="mb-3">
      <Notice tone="warning" variant="card">
        <div className="flex flex-col gap-1">
          <Text weight="bold">
            {info.sourceGenerationRequestId
              ? `本番の生成#${info.sourceGenerationRequestId}（督促ケース#${info.sourceDunningCaseId}）の入力から読み込みました（まだ保存していません）`
              : `督促ケース#${info.sourceDunningCaseId} から読み込みました（まだ保存していません）`}
          </Text>
          {info.keptCurrentItems && info.keptCurrentItems.length > 0 && (
            <div role="group" aria-label="今の値のままの項目">
              <Text size="sm" weight="bold">
                基準日時に戻せず、今の値のままの項目（説明にも残します）：
              </Text>
              <ul className="list-disc pl-6">
                {info.keptCurrentItems.map((item) => (
                  <li key={item}>
                    <Text size="sm">{item}</Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Text size="sm">
            {masked
              ? `架空の値に置き換えた項目：${masked}`
              : "登録されている氏名・電話番号・勤務先名などに一致する文字列は見つかりませんでした。"}
          </Text>
          {info.notes.map((note) => (
            <Text key={note} size="sm">
              {note}
            </Text>
          ))}
          <Text size="sm" weight="bold">
            自由記述（会話・メモ・交渉記録のメモ）に個人情報が残っていないか確認してください。住所や家族・勤務先の担当者名などは置き換えられません。
          </Text>
          <Text size="sm">
            期待する対応と確認ポイントを付けて保存すると、検証ケースになります。
          </Text>
        </div>
      </Notice>
    </div>
  )
}

/** 選んだケース1件の作業領域。読み取り表示／編集 → 生成 → 生成結果 → 生成履歴 */
export function CaseWorkspace({
  initial,
  api,
  terminals,
  onDirty,
  onBusy,
  onSaved,
  draftSource,
  onContinue,
  actions,
  onBack,
  backLabel,
  notice,
  rightHead,
  linkedResult,
}: Props) {
  const [record, setRecord] = useState(initial)
  const [editing, setEditing] = useState(!initial.id)
  const [changed, setChanged] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState("")
  const [chosenRun, setChosenRun] = useState<EvaluationRun | null>(null)
  const runs = api.detail.data?.runs ?? []
  const currentRun = chosenRun
    ? (runs.find((run) => run.id === chosenRun.id) ?? chosenRun)
    : runs[0]
  const errors = validateEvaluationCase(record)
  const invalid = Object.keys(errors).length > 0
  const historyLoading = !!record.id && !api.detail.data && !api.detail.error
  // 次の時点に足す当社の送信。表示中の生成結果に本文があればそれを使う（無ければ空欄で置く）
  const continuationRun =
    currentRun &&
    currentRun.status === "completed" &&
    currentRun.body &&
    !currentRun.generationMetadata?.humanReviewRequired
      ? currentRun
      : null

  const update = (patch: Partial<EvaluationCase>) => {
    setRecord((prev) => ({ ...prev, ...patch }))
    setChanged(true)
    onDirty(true)
  }
  /** 操作を1つ実行する。失敗したら画面に出し、呼び出し側が判断できるよう例外を返す */
  const work = async (action: () => Promise<void>): Promise<unknown> => {
    setBusy(true)
    onBusy(true)
    setError(null)
    setMessage("")
    try {
      await action()
      return undefined
    } catch (e) {
      setError(evaluationError(e))
      return e ?? new Error("unknown")
    } finally {
      setBusy(false)
      onBusy(false)
    }
  }
  const save = () =>
    work(async () => {
      if (invalid) return
      onSaved(await api.save(record))
    })
  // 生成は保存済みの版に対してだけ行う（編集中の入力を失わせない）
  const generate = async (prompt: GeneratePrompt, requestKey: string) => {
    const failure = await work(async () => {
      const result = await api.generate(record, prompt, requestKey)
      setChosenRun(result)
      setMessage(
        result.status === "running"
          ? "生成を受け付けました。完了すると自動で結果を表示します。"
          : result.status === "failed"
            ? "生成に失敗しました。失敗の記録は履歴に保存しました。"
            : "生成結果を保存しました。"
      )
    })
    return failure !== undefined && isRetryableError(failure)
  }

  const model = modelExemplars(record.expectation)[0]
  const modelBody = model?.body
  const [adopting, setAdopting] = useState<string | null>(null)
  const adopt = (body: string) => {
    setAdopting(null)
    void work(async () => {
      onSaved(
        await api.save({
          ...record,
          expectation: withModelExemplar(record.expectation, body, record.expectedTerminal, {
            status: "reference",
            source: "curated",
          }),
        })
      )
    })
  }
  const reference = <ExemplarReference expectation={record.expectation} editing={editing} />

  // 外枠（ヘッダー・左右のペイン）は督促ケース詳細と同じ。ここでは枠に置く中身だけを決める
  const slots: CaseFrameSlots = {
    onBack,
    backLabel,
    actions: (
      <>
        {/* 編集中は出さない（未保存の変更を失わせない） */}
        {!editing && record.id > 0 && onContinue && (
          <Button
            variant="outline"
            className={HEADER_BUTTON_CLASS}
            disabled={busy || historyLoading}
            onClick={() => void work(() => onContinue(continuationRun))}
          >
            次の時点を作る
          </Button>
        )}
        {actions}
        {!editing && (
          <Button
            variant="outline"
            className={HEADER_BUTTON_CLASS}
            disabled={busy}
            onClick={() => setEditing(true)}
          >
            編集
          </Button>
        )}
      </>
    ),
    // 保存・生成の結果は、どこまでスクロールしていても見えるようにヘッダーに出す
    headerExtra: (error || message || busy) && (
      <div className="mt-2 flex flex-col gap-1">
        {error && <AlertMessage>{error}</AlertMessage>}
        {message && <StatusMessage>{message}</StatusMessage>}
        {busy && <StatusMessage>処理しています…</StatusMessage>}
      </div>
    ),
    rightHead: (rightHead || (draftSource && !record.id) || notice) && (
      <div className="flex flex-col">
        {rightHead}
        {draftSource?.kind === "import" && !record.id && (
          <ImportNotice info={draftSource.preview} />
        )}
        {draftSource?.kind === "continuation" && !record.id && (
          <ContinuationNotice info={draftSource.info} />
        )}
        {notice}
      </div>
    ),
    review: (
      <>
        {linkedResult}
        <GeneratePanel
          caseId={record.id}
          lockVersion={record.lockVersion}
          changed={changed}
          busy={busy}
          onGenerate={generate}
        />
        <Section label="生成結果">
          <Heading level={3}>生成結果</Heading>
          {api.detail.error && (
            <AlertMessage>
              履歴を取得できませんでした。ページを再読み込みしてください。
            </AlertMessage>
          )}
          {historyLoading ? (
            <StatusMessage>生成履歴を読み込んでいます…</StatusMessage>
          ) : currentRun ? (
            <RunResult
              key={currentRun.id}
              run={currentRun}
              terminals={terminals}
              onInspect={api.inspect}
              reference={reference}
              onAdopt={
                // 未保存の編集と混ぜて保存しないよう、編集中は出さない
                !editing && record.id > 0 && currentRun.body?.trim() !== modelBody?.trim()
                  ? setAdopting
                  : undefined
              }
            />
          ) : !api.detail.error ? (
            <>
              <Text>まだ生成していません。</Text>
              {reference}
            </>
          ) : null}
        </Section>
        {runs.length > 0 && <RunHistory runs={runs} onChoose={setChosenRun} />}
        {adopting != null && (
          <DunningDialog
            title={
              // 置き換えたお手本は参考になるので、確認済みの正解を参考に落とすことを言う
              model?.status === "approved"
                ? "確認済みの正解のお手本を、この下書き（参考）で置き換えますか？"
                : modelBody
                  ? "今のお手本の文をこの下書きで置き換えますか？"
                  : "この下書きをお手本の文にしますか？"
            }
            onClose={() => setAdopting(null)}
            footer={
              <>
                <Button variant="outline" onClick={() => setAdopting(null)}>
                  キャンセル
                </Button>
                <Button onClick={() => adopt(adopting)}>
                  {modelBody ? "置き換える" : "保存する"}
                </Button>
              </>
            }
          >
            {null}
          </DunningDialog>
        )}
      </>
    ),
  }

  // 枠を画面の残りの高さに収めるため、xl では縦並びの flex で高さを枠へ渡す
  return (
    <fieldset disabled={busy} className="min-w-0 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      {editing ? (
        <CaseEditForm
          record={record}
          terminals={terminals}
          errors={errors}
          busy={busy}
          changed={changed}
          onChange={update}
          onSave={save}
          onClose={() => setEditing(false)}
          {...slots}
        />
      ) : (
        <CaseReadView record={record} terminals={terminals} {...slots} />
      )}
    </fieldset>
  )
}
